import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Github, Layers3 } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import TiltCard from './TiltCard';
import SectionHeading from './SectionHeading';

const projects = [
  {
    id: 1,
    title: 'Spam Classification ML Models',
    description: 'Built spam detection models using KNN, Logistic Regression, and SVM with hyperparameter tuning in Python. Achieved 96%+ validation accuracy using Stratified K-Fold CV, confusion matrix analysis in Jupyter Notebook.',
    image: '/Images/SpamClassification.png',
    github: 'https://github.com/ShivanshMane/SpamClassification',
    live: null,
    technologies: ['Python', 'KNN', 'Logistic Regression', 'SVM', 'Jupyter'],
    category: 'AI/ML'
  },
  {
    id: 2,
    title: 'Fitness Dashboard',
    description: 'Built a robust fitness tracker app using Vue 3, Vue Router, and Firebase Firestore for real-time workout tracking. Successfully used Pinia for state management and APIs with HTML, CSS, and Bootstrap to enhance user experience.',
    image: '/Images/FitnessDashboard.png',
    github: 'https://github.com/ShivanshMane/Fitness-Dashboard',
    live: null,
    technologies: ['Vue 3', 'Firebase', 'Pinia', 'Bootstrap', 'CSS'],
    category: 'Web Development'
  },
  {
    id: 3,
    title: 'EduNavi',
    description: 'Leverages advanced AI/ML algorithms using Python to efficiently generate summaries, reducing review time by 40%. Enhances user experience with JavaScript, CSS, and HTML, improving learning efficiency by 30% boosting efficiency.',
    image: '/Images/EduNavi.png',
    github: 'https://github.com/ShivanshMane/EduNavi',
    live: null,
    technologies: ['Python', 'AI/ML', 'JavaScript', 'CSS', 'HTML'],
    category: 'AI/ML'
  },
  {
    id: 4,
    title: 'Amazon Clone',
    description: 'A full-stack e-commerce platform built with modern web technologies. Features include user authentication, product catalog, shopping cart, payment integration, and admin dashboard.',
    image: '/Images/AmazonClone.png',
    github: 'https://github.com/ShivanshMane/Amazon-Clone',
    live: null,
    technologies: ['React', 'Node.js', 'MongoDB', 'Express', 'Stripe'],
    category: 'Web Development'
  },
  {
    id: 5,
    title: 'DetectAI',
    description: 'Advanced AI-powered image detection and classification system. Uses deep learning models to identify objects, faces, and patterns in images with high accuracy.',
    image: '/Images/DetectAI.jpg',
    github: 'https://github.com/ShivanshMane/DetectAI',
    live: null,
    technologies: ['Python', 'TensorFlow', 'OpenCV', 'Deep Learning', 'Computer Vision'],
    category: 'AI/ML'
  },
  {
    id: 6,
    title: 'Stroke Predictor',
    description: 'Machine learning model to predict stroke risk based on medical data. Uses ensemble methods and feature engineering to provide accurate predictions for healthcare applications.',
    image: '/Images/StrokePredictor.png',
    github: 'https://github.com/ShivanshMane/Stroke-Predictor',
    live: null,
    technologies: ['Python', 'Scikit-learn', 'Pandas', 'NumPy', 'Healthcare ML'],
    category: 'AI/ML'
  },


];

const categories = ['All', 'AI/ML', 'Web Development'];

const Projects: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [ref, isInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <section id="projects" className="relative py-24 overflow-hidden">
      <div className="section-shell" ref={ref}>
        <SectionHeading
          eyebrow="Projects"
          title={<>A product-minded portfolio of <span className="gradient-text-vibrant">engineering experiments and shipped ideas.</span></>}
          description="Each project pushes on a different axis: AI accuracy, product design, frontend polish, backend architecture, or developer workflow."
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-12 flex flex-wrap justify-center gap-3"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                activeFilter === category
                  ? 'bg-aurora-borealis text-slate-950 shadow-aurora-emerald'
                  : 'glass text-slate-700 hover:border-cosmic-aurora-emerald/40 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid gap-8 md:grid-cols-2 xl:grid-cols-3"
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group"
              >
                <TiltCard intensity={2}>
                  <div className="surface-panel surface-glow spotlight-card overflow-hidden rounded-[30px] p-0 transition-all duration-300 hover:border-cosmic-aurora-emerald/50">
                    <div className="relative h-56 overflow-hidden">
                      <div className="absolute inset-0 z-10 bg-gradient-to-t from-slate-950/90 via-slate-950/10 to-transparent" />
                      <div className="absolute inset-0 z-10 bg-gradient-to-br from-cosmic-aurora-emerald/12 to-cosmic-aurora-amethyst/18 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      <img
                        src={project.image}
                        alt={project.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />

                      <div className="absolute left-5 top-5 z-20">
                        <span className="badge-pill border-cosmic-aurora-emerald/25 text-cosmic-aurora-emerald">
                          {project.category}
                        </span>
                      </div>

                      <div className="absolute bottom-5 left-5 right-5 z-20 flex items-center justify-between gap-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <motion.a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="glass flex h-11 w-11 items-center justify-center rounded-full hover:bg-cosmic-aurora-emerald/20"
                        >
                          <Github size={20} className="text-white" />
                        </motion.a>
                        {project.live && (
                          <motion.a
                            href={project.live}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="glass flex h-11 w-11 items-center justify-center rounded-full hover:bg-cosmic-aurora-amethyst/20"
                          >
                            <ArrowUpRight size={20} className="text-white" />
                          </motion.a>
                        )}
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="mb-3 flex items-center gap-3">
                        <div className="rounded-2xl bg-cosmic-aurora-sapphire/12 p-2 text-cosmic-aurora-sapphire">
                          <Layers3 className="h-4 w-4" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 transition-colors duration-300 group-hover:text-cosmic-aurora-emerald dark:text-white">
                          {project.title}
                        </h3>
                      </div>
                      
                      <p className="mb-5 text-sm leading-7 text-slate-700 dark:text-slate-300">
                        {project.description}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech) => (
                          <span
                            key={tech}
                            className="badge-pill border-cosmic-aurora-emerald/20 text-cosmic-aurora-emerald"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Projects;
